import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { objectsApi } from "../services/api";
import { CategorySelect, ColorSelect } from "../components/SelectField";
import BottomNav from "../components/BottomNav";
import toast from "react-hot-toast";

const T = { night:"#0A1F3D", royal:"#1263B0", cyan:"#1CBCD3", gold:"#C9922A",
  white:"#fff", bg:"#F7F9FC", border:"#E2E8F0" };

export default function DeclareLoss() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    category:"", objectName:"", description:"", mainColor:"",
    brandModel:"", serialNumber:"", lostLocation:"", lostDate:"",
    lostDuringTravel:false, departureCity:"", arrivalCity:"", travelCountry:""
  });

  const set = (k) => (e) => setForm(f => ({...f, [k]: e.target.value}));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const r = await objectsApi.declareLost(form);
      toast.success(t("declare_loss.success") + " " + (r.data?.data?.reference || ""));
      nav("/dashboard");
    } catch { toast.error(t("declare_loss.error")); }
    finally { setLoading(false); }
  };

  const inp = (label, k, ph, optional, type="text") => (
    <div key={k} style={{ marginBottom:"16px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"6px" }}>
        <label style={{ fontSize:"13px", fontWeight:600, color:"#334155" }}>{label}</label>
        {optional && <span style={{ fontSize:"11px", color:"#94A3B8" }}>{t("common.optional")}</span>}
      </div>
      <input type={type} value={form[k]} onChange={set(k)}
        placeholder={ph} spellCheck={false}
        style={{ width:"100%", padding:"12px 16px", border:"1.5px solid #E2E8F0",
          borderRadius:"12px", fontSize:"14px", outline:"none",
          boxSizing:"border-box", background:"#fff" }}/>
    </div>
  );

  const canStep2 = form.category && form.objectName && form.description;
  const canStep3 = form.lostLocation && form.lostDate;

  return (
    <div style={{ minHeight:"100vh", background:T.bg,
      fontFamily:"'DM Sans',sans-serif", paddingBottom:"80px" }}>

      {/* HEADER */}
      <div style={{ background:`linear-gradient(135deg,${T.night},${T.royal})`,
        padding:"20px 24px", display:"flex", alignItems:"center", gap:"16px" }}>
        <button onClick={()=>step>1?setStep(s=>s-1):nav("/dashboard")}
          style={{ background:"transparent", border:"none",
            color:T.white, fontSize:"20px", cursor:"pointer" }}>←</button>
        <div>
          <h1 style={{ color:T.white, fontSize:"17px", fontWeight:700, margin:0 }}>
            {t("declare_loss.title")}
          </h1>
          <p style={{ color:"rgba(255,255,255,0.6)", fontSize:"12px", margin:0 }}>
            {t("declare_loss.step_of")} {step} {t("declare_loss.of")} 4
          </p>
        </div>
      </div>

      <div style={{ maxWidth:"600px", margin:"0 auto", padding:"24px" }}>

        {/* STEP BAR */}
        <div style={{ display:"flex", gap:"6px", marginBottom:"24px" }}>
          {[1,2,3,4].map(s => (
            <div key={s} style={{ flex:1, height:"4px", borderRadius:"4px",
              background:s<step?T.gold:s===step?T.cyan:"#E2E8F0" }}/>
          ))}
        </div>

        {/* STEP 1 - ATTESTATION */}
        {step===1 && (
          <div>
            <h2 style={{ fontSize:"16px", fontWeight:700, color:T.night, marginBottom:"16px" }}>
              {t("declare_loss.attestation_title")}
            </h2>
            <div style={{ background:T.white, borderRadius:"12px", padding:"16px",
              fontSize:"13px", lineHeight:1.7, color:"#334155",
              border:"1px solid #E2E8F0", marginBottom:"16px" }}>
              {t("declare_loss.attestation_text")}
            </div>
            <label style={{ display:"flex", gap:"10px", alignItems:"flex-start",
              fontSize:"13px", color:"#334155", marginBottom:"20px", cursor:"pointer" }}>
              <input type="checkbox" checked={checked}
                onChange={e=>setChecked(e.target.checked)}
                style={{ marginTop:"3px", width:"16px", height:"16px" }}/>
              {t("declare_loss.certify_check")}
            </label>
            <button onClick={()=>setStep(2)} disabled={!checked}
              style={{ width:"100%", padding:"14px",
                background:checked?T.royal:"#E2E8F0",
                color:checked?T.white:"#94A3B8",
                border:"none", borderRadius:"12px", fontWeight:700,
                cursor:checked?"pointer":"not-allowed", fontSize:"15px" }}>
              {t("declare_loss.continue")}
            </button>
          </div>
        )}

        {/* STEP 2 - DESCRIPTION */}
        {step===2 && (
          <div>
            <div style={{ background:"#E0F7FA", borderLeft:"4px solid #1CBCD3",
              padding:"12px 16px", borderRadius:"0 12px 12px 0",
              fontSize:"13px", color:"#0E8FA3", marginBottom:"20px" }}>
              {t("declare_loss.matching_hint")}
            </div>

            <div style={{ marginBottom:"16px" }}>
              <label style={{ fontSize:"13px", fontWeight:600, color:"#334155",
                marginBottom:"6px", display:"block" }}>
                {t("declare_loss.category")}
              </label>
              <CategorySelect value={form.category}
                onChange={e=>setForm(f=>({...f,category:e.target.value}))} />
            </div>

            {inp(t("declare_loss.object_name"), "objectName",
              t("declare_loss.object_placeholder"))}

            <div style={{ marginBottom:"16px" }}>
              <label style={{ fontSize:"13px", fontWeight:600, color:"#334155",
                marginBottom:"6px", display:"block" }}>
                {t("declare_loss.description")}
              </label>
              <textarea value={form.description}
                onChange={e=>setForm(f=>({...f,description:e.target.value}))}
                placeholder={t("declare_loss.description_placeholder")}
                rows={4} spellCheck={false}
                style={{ width:"100%", padding:"12px 16px",
                  border:"1.5px solid #E2E8F0", borderRadius:"12px",
                  fontSize:"14px", outline:"none",
                  boxSizing:"border-box", resize:"none", background:"#fff" }}/>
            </div>

            <div style={{ marginBottom:"16px" }}>
              <label style={{ fontSize:"13px", fontWeight:600, color:"#334155",
                marginBottom:"6px", display:"block" }}>
                {t("declare_loss.main_color")}
              </label>
              <ColorSelect value={form.mainColor}
                onChange={e=>setForm(f=>({...f,mainColor:e.target.value}))} />
            </div>

            {inp(t("declare_loss.brand_model"), "brandModel",
              t("declare_loss.brand_placeholder"), true)}
            {inp(t("declare_loss.serial"), "serialNumber",
              t("declare_loss.serial_placeholder"), true)}

            <button onClick={()=>setStep(3)} disabled={!canStep2}
              style={{ width:"100%", padding:"14px",
                background:canStep2?T.royal:"#E2E8F0",
                color:canStep2?T.white:"#94A3B8",
                border:"none", borderRadius:"12px", fontWeight:700,
                cursor:canStep2?"pointer":"not-allowed", fontSize:"15px" }}>
              {t("declare_loss.continue")}
            </button>
          </div>
        )}

        {/* STEP 3 - LIEU ET DATE */}
        {step===3 && (
          <div>
            {inp(t("declare_loss.lost_location"), "lostLocation",
              t("declare_loss.location_placeholder"))}

            <div style={{ marginBottom:"20px" }}>
              <label style={{ fontSize:"13px", fontWeight:600, color:"#334155",
                marginBottom:"6px", display:"block" }}>
                {t("declare_loss.lost_date")}
              </label>
              <input type="date" value={form.lostDate}
                onChange={e=>setForm(f=>({...f,lostDate:e.target.value}))}
                style={{ width:"100%", padding:"12px 16px",
                  border:"1.5px solid #E2E8F0", borderRadius:"12px",
                  fontSize:"14px", outline:"none",
                  boxSizing:"border-box", background:"#fff" }}/>
            </div>

            <div style={{ marginBottom:"20px", padding:"16px",
              background:T.white, borderRadius:"12px", border:"1px solid #E2E8F0" }}>
              <div style={{ fontSize:"13px", fontWeight:600, color:"#334155",
                marginBottom:"10px" }}>
                {t("declare_loss.travel_section")}
              </div>
              <label style={{ display:"flex", gap:"10px", alignItems:"flex-start",
                fontSize:"13px", color:"#334155", cursor:"pointer" }}>
                <input type="checkbox" checked={form.lostDuringTravel}
                  onChange={e=>setForm(f=>({...f,lostDuringTravel:e.target.checked}))}
                  style={{ marginTop:"3px", width:"16px", height:"16px" }}/>
                {t("declare_loss.travel_check")}
              </label>

              {form.lostDuringTravel && (
                <div style={{ marginTop:"16px" }}>
                  {inp(t("declare_loss.departure_city"), "departureCity",
                    t("declare_loss.departure_placeholder"), true)}
                  {inp(t("declare_loss.arrival_city"), "arrivalCity",
                    t("declare_loss.arrival_placeholder"), true)}
                  {inp(t("declare_loss.travel_country"), "travelCountry",
                    t("declare_loss.country_placeholder"), true)}
                </div>
              )}
            </div>

            <button onClick={()=>setStep(4)} disabled={!canStep3}
              style={{ width:"100%", padding:"14px",
                background:canStep3?T.royal:"#E2E8F0",
                color:canStep3?T.white:"#94A3B8",
                border:"none", borderRadius:"12px", fontWeight:700,
                cursor:canStep3?"pointer":"not-allowed", fontSize:"15px" }}>
              {t("declare_loss.continue")}
            </button>
          </div>
        )}

        {/* STEP 4 - CONFIRMATION */}
        {step===4 && (
          <div>
            <h2 style={{ fontSize:"16px", fontWeight:700,
              color:T.night, marginBottom:"16px" }}>
              {t("declare_loss.verify_title")}
            </h2>
            {[
              [t("declare_loss.category"), form.category],
              [t("declare_loss.object_name"), form.objectName],
              [t("declare_loss.description"), form.description],
              [t("declare_loss.lost_location"), form.lostLocation],
              [t("declare_loss.lost_date"), form.lostDate],
              ...(form.lostDuringTravel ? [
                [t("declare_loss.departure_city"), form.departureCity],
                [t("declare_loss.arrival_city"), form.arrivalCity],
                [t("declare_loss.travel_country"), form.travelCountry]
              ] : [])
            ].map(([label, val]) => val && (
              <div key={label} style={{ marginBottom:"12px", padding:"12px 16px",
                background:T.white, borderRadius:"10px",
                border:"1px solid #E2E8F0" }}>
                <div style={{ fontSize:"11px", color:"#94A3B8",
                  marginBottom:"4px" }}>{label}</div>
                <div style={{ fontSize:"14px", color:T.night,
                  fontWeight:500 }}>{val}</div>
              </div>
            ))}
            <div style={{ background:"#FBF4E8", borderLeft:"4px solid #C9922A",
              padding:"12px 16px", borderRadius:"0 12px 12px 0",
              fontSize:"13px", color:"#A67820", margin:"16px 0" }}>
              {t("declare_loss.free_note")}
            </div>
            <button onClick={handleSubmit} disabled={loading}
              style={{ width:"100%", padding:"16px",
                background:loading?"#E2E8F0":T.royal,
                color:loading?"#94A3B8":T.white,
                border:"none", borderRadius:"12px", fontWeight:700,
                cursor:loading?"not-allowed":"pointer", fontSize:"15px" }}>
              {loading?t("common.loading"):t("declare_loss.submit")}
            </button>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
